import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { useWidget } from "/imports/state/WidgetContext";

import { EmptyState, InstallAppCard } from "/imports/ui/components/shared";
import TrainerWeekGrid from "/imports/ui/views/TrainerWeekGrid";
import { NO_CAPTURE } from "/imports/libs/analytics";

import useTrainerSchedule from "./useTrainerSchedule";
import { EMPTY_TEXT } from "./trainerScheduleHelpers";
import ScheduleHeader from "./components/ScheduleHeader";
import ListRangeControls from "./components/ListRangeControls";
import WeekNavCard from "./components/WeekNavCard";
import ScheduleList from "./components/ScheduleList";
import AttendanceDialog from "./components/AttendanceDialog";
import BookingNoteDialog from "./components/BookingNoteDialog";

function TrainerDashboardView() {
  const s = useTrainerSchedule();
  const { slug } = useWidget();
  const navigate = useNavigate();
  const [attendanceBooking, setAttendanceBooking] = useState(null);
  const [noteBooking, setNoteBooking] = useState(null);
  const isSenior = Boolean(s.data?.member?.isSenior);

  const openNote = (booking) => {
    setAttendanceBooking(null);
    setNoteBooking(booking);
  };

  return (
    <Box className={NO_CAPTURE}>
      {/* Trainers subscribe too (D4): new bookings and cancellations on their own phone. */}
      <InstallAppCard audience="trainer" />
      <ScheduleHeader
        firstName={s.firstName}
        viewMode={s.viewMode}
        onViewChange={s.handleViewChange}
        loading={s.loading}
        onRefresh={s.refresh}
        onOpenFullSchedule={isSenior ? () => navigate(`/${slug}/trainer/schedule`) : undefined}
      />

      {s.isWeekMode ? (
        <WeekNavCard
          weekLabel={s.weekLabel}
          weekAnchor={s.weekAnchor}
          loading={s.loading}
          onShiftWeek={s.shiftWeek}
          onResetWeek={() => s.setWeekAnchor(null)}
        />
      ) : (
        <ListRangeControls
          isRangeMode={s.isRangeMode}
          fromDate={s.fromDate}
          setFromDate={s.setFromDate}
          toDate={s.toDate}
          setToDate={s.setToDate}
          rangeScope={s.rangeScope}
          setRangeScope={s.setRangeScope}
          loading={s.loading}
          onLoadRange={s.handleLoadRange}
          onClearRange={s.handleClearRange}
        />
      )}

      {s.loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : s.error ? (
        <EmptyState title="Couldn't load your schedule" description={s.error.message || "Please try again in a moment."} />
      ) : s.isWeekMode ? (
        <TrainerWeekGrid
          days={s.days}
          weekStart={s.data?.weekStart}
          todayYmd={s.data?.todayYmd}
          timeZone={s.data?.timeZone}
          onMark={setAttendanceBooking}
          onNote={openNote}
        />
      ) : s.days.length === 0 ? (
        <EmptyState title="No bookings" description={EMPTY_TEXT[s.activeScope] || EMPTY_TEXT.all} />
      ) : (
        <ScheduleList
          days={s.days}
          data={s.data}
          page={s.page}
          setPage={s.setPage}
          total={s.total}
          totalPages={s.totalPages}
          onMark={setAttendanceBooking}
          onNote={openNote}
        />
      )}

      <AttendanceDialog booking={attendanceBooking} onClose={() => setAttendanceBooking(null)} onChanged={s.applyAttendance} onNote={openNote} />
      <BookingNoteDialog booking={noteBooking} onClose={() => setNoteBooking(null)} onSaved={s.applyNote} />
    </Box>
  );
}

export default TrainerDashboardView;
